import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import classnames from 'classnames';
import {
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  UncontrolledDropdown,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import UpgradePlanButton from './UpgradePlanButton';

import {
  userCan,
  userHasCap,
  userIsAdmin,
  userIsManager,
} from '../helpers/Auth';
import { getSetting } from '../helpers/Settings';
import { getIntegrationsURLPath } from '../helpers/Integrations';
import { upgradeModalIfNeeded } from '../helpers/UpgradePlan';

const MoreMenu = ({ location, profile, toggleNavigation }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  if (!profile) {
    return null;
  }

  const { pathname } = location;
  const paths = [
    '/archive/files',
    '/downloads',
    '/uploads',
    '/admin/reports',
    '/admin/ftp-accounts',
    '/admin/ftp-queue',
    '/integrations',
    '/synced-collections',
  ];

  const isActive = (p) => (
    pathname.indexOf(p) === 0
  );

  const isMoreActive = () => {
    let active = false;
    paths.forEach((path) => {
      if (pathname.indexOf(path) === 0) {
        active = true;
      }
    });
    return active;
  };

  return (
    <UncontrolledDropdown
      nav
      className={classnames({ active: isOpen || isMoreActive() })}
      direction="right"
      onToggle={(e, isToggleOpen) => {
        setIsOpen(isToggleOpen);
        if (!isToggleOpen) {
          toggleNavigation();
        }
      }}
    >
      <DropdownToggle
        nav
        className="nav-more-menu"
      >
        <span className="nav-item-icon">
          <i className="far fa-ellipsis-h" />
        </span>
        <span className="nav-item-title">
          {intl.formatMessage({ defaultMessage: 'More' })}
        </span>
      </DropdownToggle>
      <DropdownMenu className="more-menu-dropdown" right container="body">
        {(userIsAdmin() || userHasCap('browse_archived_files')) && (
          <DropdownItem
            tag={Link}
            to="/archive/files"
            className={classnames({ active: isActive('/archive/files') })}
            onClick={(e) => upgradeModalIfNeeded('archive', () => {}, e)}
          >
            <i className="far fa-archive" />
            <div className="more-item-wrapper">
              <span className="more-item-title">
                {intl.formatMessage({ defaultMessage: 'Archive' })}
                <UpgradePlanButton
                  feature="archive"
                  simple={true}
                />
              </span>
              <span className="more-item-subtitle">
                {intl.formatMessage({ defaultMessage: 'Access archived files and Collections' })}
              </span>
            </div>
          </DropdownItem>
        )}
        <DropdownItem
          tag={Link}
          to="/downloads"
          className={classnames({ active: isActive('/downloads') })}
        >
          <i className="far fa-download" />
          <div className="more-item-wrapper">
            <span className="more-item-title">
              {
                !userIsAdmin() ?
                  intl.formatMessage({ defaultMessage: 'My Downloads' }) :
                  intl.formatMessage({ defaultMessage: 'Downloads' })
              }
            </span>
            <span className="more-item-subtitle">
              {
                userIsAdmin() ?
                  intl.formatMessage({ defaultMessage: 'View all workspace downloads' }) :
                  intl.formatMessage({ defaultMessage: 'View my downloads' })
              }
            </span>
          </div>
        </DropdownItem>
        {userHasCap('upload_file') && (
          <DropdownItem
            tag={Link}
            to="/uploads"
            className={classnames({ active: isActive('/uploads') })}
          >
            <i className="far fa-upload" />
            <div className="more-item-wrapper">
              <span className="more-item-title">
                {
                  !userIsAdmin() ?
                    intl.formatMessage({ defaultMessage: 'My Uploads' }) :
                    intl.formatMessage({ defaultMessage: 'Uploads' })
                }
              </span>
              <span className="more-item-subtitle">
                {
                  userIsAdmin() ?
                    intl.formatMessage({ defaultMessage: 'View all workspace uploads' }) :
                    intl.formatMessage({ defaultMessage: 'View my uploads' })
                }
              </span>
            </div>
          </DropdownItem>
        )}
        {(userIsAdmin() || userIsManager() || userHasCap('reports')) && (
          <DropdownItem
            tag={Link}
            to="/admin/reports"
            className={classnames({ active: isActive('/admin/reports') })}
            onClick={(e) => upgradeModalIfNeeded('reports', () => {}, e)}
          >
            <i className="far fa-file-chart-pie" />
            <div className="more-item-wrapper">
              <span className="more-item-title">
                {intl.formatMessage({ defaultMessage: 'Reports' })}
                <UpgradePlanButton
                  feature="reports"
                  simple={true}
                />
              </span>
              <span className="more-item-subtitle">
                {intl.formatMessage({ defaultMessage: 'Generate workspace usage reports' })}
              </span>
            </div>
          </DropdownItem>
        )}
        {((userIsAdmin() || userIsManager() || userHasCap('ftp')) && getSetting('enable_ftp_importer')) && (
          <>
            <DropdownItem
              tag={Link}
              to="/admin/ftp-accounts"
              className={classnames({ active: isActive('/admin/ftp-accounts') })}
              onClick={(e) => upgradeModalIfNeeded('ftp', () => {}, e)}
            >
              <i className="far fa-server" />
              <div className="more-item-wrapper">
                <span className="more-item-title">
                  {intl.formatMessage({ defaultMessage: 'FTP accounts' })}
                  <UpgradePlanButton
                    feature="ftp"
                    simple={true}
                  />
                </span>
                <span className="more-item-subtitle">
                  {intl.formatMessage({ defaultMessage: 'Create and manage FTP accounts' })}
                </span>
              </div>
            </DropdownItem>
            <DropdownItem
              tag={Link}
              to="/admin/ftp-queue"
              className={classnames({ active: isActive('/admin/ftp-queue') })}
              onClick={(e) => upgradeModalIfNeeded('ftp', () => {}, e)}
            >
              <i className="far fa-file-arrow-up" />
              <div className="more-item-wrapper">
                <span className="more-item-title">
                  {intl.formatMessage({ defaultMessage: 'FTP queue' })}
                  <UpgradePlanButton
                    feature="ftp"
                    simple={true}
                  />
                </span>
                <span className="more-item-subtitle">
                  {intl.formatMessage({ defaultMessage: 'Monitor your FTP queue' })}
                </span>
              </div>
            </DropdownItem>
          </>
        )}
        {(!getSetting('cs1') && userCan('integrations')) && (
          <DropdownItem
            tag={Link}
            to={getIntegrationsURLPath()}
            className={classnames({ active: isActive(getIntegrationsURLPath()) })}
          >
            <i className="far fa-circle-nodes" />
            <div className="more-item-wrapper">
              <span className="more-item-title">
                {intl.formatMessage({ defaultMessage: 'Integrations' })}
              </span>
              <span className="more-item-subtitle">
                {intl.formatMessage({ defaultMessage: 'Connect to external services' })}
              </span>
            </div>
          </DropdownItem>
        )}
        {userCan('desktop_sync') && (
          <DropdownItem
            tag={Link}
            to="/synced-collections"
            className={classnames({ active: isActive('/synced-collections') })}
          >
            <i className="far fa-rotate" />
            <div className="more-item-wrapper">
              <span className="more-item-title">
                {intl.formatMessage({ defaultMessage: 'Synced Collections' })}
              </span>
              <span className="more-item-subtitle">
                {intl.formatMessage({ defaultMessage: 'Manage your synced collections' })}
              </span>
            </div>
          </DropdownItem>
        )}
        <DropdownItem
          tag={Link}
          to="/trash"
          className={classnames({ active: isActive('/trash') })}
        >
          <i className="far fa-trash" />
          <div className="more-item-wrapper">
            <span className="more-item-title">
              {intl.formatMessage({ defaultMessage: 'Trash' })}
            </span>
            <span className="more-item-subtitle">
              {intl.formatMessage({ defaultMessage: 'Access deleted files and Collections' })}
            </span>
          </div>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

MoreMenu.propTypes = {
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  toggleNavigation: PropTypes.func.isRequired,
};

MoreMenu.defaultProps = {
  profile: null,
};

export default withRouter(connect(mapStateToProps)(MoreMenu));
